import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  type RecurringCommissionDecision,
  useDecideRecurringCommissionAction,
} from '@/hooks/actions/use-decide-recurring-commission-action';
import { Badge, Box, Button, Field, Flex, HStack, Text, Textarea, VStack } from '@chakra-ui/react';
import {
  formatExactCommissionMoney,
  getCommissionSourcePresentation,
  type Commission,
} from '@fluenceur/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type DecisionDialog = RecurringCommissionDecision | null;

const decisionColorPalette = {
  pending: 'yellow',
  validated: 'green',
  rejected: 'red',
} as const;

const actorName = (commission: Commission) => {
  const actor = commission.decidedBy;
  if (!actor) return null;
  return [actor.firstName, actor.lastName].filter(Boolean).join(' ') || actor.id;
};

export function RecurringCommissionDetailsDrawer({
  commission,
  open,
  onClose,
}: {
  commission: Commission | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = useState<DecisionDialog>(null);
  const [approvalNote, setApprovalNote] = useState('');
  const decision = useDecideRecurringCommissionAction();

  if (!commission || commission.source?.kind !== 'recurring') return null;

  const source = getCommissionSourcePresentation(commission);
  const validationStatus = commission.validationStatus ?? 'pending';
  const canDecide = validationStatus === 'pending';
  const decidedBy = actorName(commission);
  const decisionDate = commission.decidedAt
    ? new Date(commission.decidedAt).toLocaleString(i18n.language)
    : null;

  const submit = (nextDecision: RecurringCommissionDecision, reason?: string) => {
    decision.mutate(
      {
        commissionId: commission.id,
        decision: nextDecision,
        reason: reason?.trim() || undefined,
      },
      {
        onSuccess: () => {
          setDialog(null);
          setApprovalNote('');
          // The drawer only owns a snapshot from the history list. Close it so
          // the next open comes from the refetched, immutable decision record.
          onClose();
        },
      }
    );
  };

  return (
    <>
      <DrawerRoot open={open} onOpenChange={(event) => !event.open && onClose()} placement="end">
        <DrawerBackdrop />
        <DrawerContent maxW="480px">
          <DrawerCloseTrigger />
          <DrawerHeader>
            <DrawerTitle>{t('commissions.decision.detail.title')}</DrawerTitle>
          </DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" gap={6}>
              <VStack align="stretch" gap={2}>
                <Text fontWeight="semibold">{t('commissions.decision.detail.subscription')}</Text>
                <Text fontSize="sm" color="fg.muted">
                  {source.reference}
                </Text>
                {source.servicePeriod ? (
                  <Text fontSize="sm" color="fg.muted">
                    {t('commissions.history.table.servicePeriod', {
                      startsAt: new Date(source.servicePeriod.startsAt).toLocaleDateString(
                        i18n.language,
                        { dateStyle: 'medium', timeZone: 'UTC' }
                      ),
                      endsAt: new Date(source.servicePeriod.endsAt).toLocaleDateString(
                        i18n.language,
                        { dateStyle: 'medium', timeZone: 'UTC' }
                      ),
                    })}
                  </Text>
                ) : null}
              </VStack>

              <Box
                data-testid="recurring-commission-position"
                borderTopWidth="1px"
                borderColor="border"
                pt={5}
              >
                <VStack align="stretch" gap={3}>
                  <Text fontWeight="semibold">
                    {t('commissions.decision.detail.commissionPosition')}
                  </Text>
                  <DecisionFact
                    label={t('commissions.decision.detail.originalCommission')}
                    value={formatExactCommissionMoney(
                      commission.source.commissionPosition.original,
                      i18n.language
                    )}
                  />
                  <DecisionFact
                    label={t('commissions.decision.detail.reversedAmount')}
                    value={formatExactCommissionMoney(
                      commission.source.commissionPosition.reversed,
                      i18n.language
                    )}
                  />
                  <DecisionFact
                    label={t('commissions.decision.detail.netPayable')}
                    value={formatExactCommissionMoney(
                      commission.source.commissionPosition.net,
                      i18n.language
                    )}
                  />
                  {commission.payoutAmountExact ? (
                    <DecisionFact
                      label={t('commissions.decision.detail.frozenPayoutAmount')}
                      value={formatExactCommissionMoney(
                        commission.payoutAmountExact,
                        i18n.language
                      )}
                    />
                  ) : null}
                  <DecisionFact
                    label={t('commissions.decision.detail.correctionCount')}
                    value={commission.source.commissionPosition.correctionCount}
                  />
                </VStack>
              </Box>

              <Box borderTopWidth="1px" borderColor="border" pt={5}>
                <VStack align="stretch" gap={3}>
                  <Text fontWeight="semibold">{t('commissions.decision.detail.decision')}</Text>
                  <HStack>
                    <Badge colorPalette={decisionColorPalette[validationStatus]} variant="subtle">
                      {t(`commissions.decision.status.${validationStatus}`)}
                    </Badge>
                  </HStack>
                  {decidedBy ? (
                    <DecisionFact
                      label={t('commissions.decision.detail.decidedBy')}
                      value={decidedBy}
                    />
                  ) : null}
                  {decisionDate ? (
                    <DecisionFact
                      label={t('commissions.decision.detail.decidedAt')}
                      value={decisionDate}
                    />
                  ) : null}
                  {commission.validationNote ? (
                    <DecisionFact
                      label={t(
                        validationStatus === 'rejected'
                          ? 'commissions.decision.detail.reason'
                          : 'commissions.decision.detail.note'
                      )}
                      value={commission.validationNote}
                    />
                  ) : null}
                </VStack>
              </Box>
            </VStack>
          </DrawerBody>
          {canDecide ? (
            <DrawerFooter borderTopWidth="1px" borderColor="border">
              <Flex gap={3} w="full" justify="flex-end">
                <Button
                  variant="outline"
                  colorPalette="red"
                  disabled={decision.isPending}
                  onClick={() => setDialog('rejected')}
                >
                  {t('commissions.decision.actions.reject')}
                </Button>
                <Button disabled={decision.isPending} onClick={() => setDialog('approved')}>
                  {t('commissions.decision.actions.approve')}
                </Button>
              </Flex>
            </DrawerFooter>
          ) : null}
        </DrawerContent>
      </DrawerRoot>

      <ConfirmDialog
        isOpen={dialog === 'approved'}
        title={t('commissions.decision.approveDialog.title')}
        description={
          <Field.Root>
            <Field.Label>{t('commissions.decision.approveDialog.noteLabel')}</Field.Label>
            <Textarea
              value={approvalNote}
              onChange={(event) => setApprovalNote(event.target.value)}
              placeholder={t('commissions.decision.approveDialog.notePlaceholder')}
              rows={4}
              aria-label={t('commissions.decision.approveDialog.noteLabel')}
            />
          </Field.Root>
        }
        confirmText={t('commissions.decision.actions.approve')}
        isLoading={decision.isPending}
        onCancel={() => {
          setDialog(null);
          setApprovalNote('');
        }}
        onConfirm={() => submit('approved', approvalNote)}
      />

      <ReasonDialog
        open={dialog === 'rejected'}
        title={t('commissions.decision.rejectDialog.title')}
        description={t('commissions.decision.rejectDialog.description')}
        reasonLabel={t('commissions.decision.rejectDialog.reasonLabel')}
        reasonPlaceholder={t('commissions.decision.rejectDialog.reasonPlaceholder')}
        confirmLabel={t('commissions.decision.actions.reject')}
        isLoading={decision.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) => submit('rejected', reason)}
        testId="recurring-commission-reject-dialog"
      />
    </>
  );
}

function DecisionFact({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fontSize="xs" color="fg.muted">
        {label}
      </Text>
      <Text fontSize="sm" whiteSpace="pre-wrap">
        {value}
      </Text>
    </Box>
  );
}
